import { alturasDosPatamares, ANCORAGEM_ACIMA_DO_PATAMAR_M, cantoDoAlcapao } from './torre'
import { pontoFlexionado } from './flexaoEscadaGeometria'
import { parametrosDoObjeto } from './parametros'
import { geometriaDoEnvolvimento } from './envolvimento'
import { disposicaoDoSistema } from './vantagemMecanica'
import { ESPESSURA_MURETA_M, formaDaCisterna, medidasDoCanal, perfilDoTerreno } from './terreno'
import type { CenaTabletop, ObjetoTabletop, ReferenciaPonto, VetorTabletop } from './tipos'

export type { ReferenciaPonto }

/**
 * Pontos de ancoragem dos objetos do table top.
 *
 * Um ponto de ancoragem é um lugar do objeto onde se pode prender, passar ou
 * pendurar algo — a ponta de uma corda, um mosquetão, a maca. A corda (fase 4
 * do plano) guarda `objeto + ponto`, nunca coordenadas: assim ela acompanha o
 * objeto quando ele se move ou muda de forma.
 *
 * Tudo aqui é TypeScript puro: sem React e sem Three.js. A cena 3D desenha os
 * marcadores com as mesmas coordenadas locais, dentro do grupo do objeto, então
 * o que se vê e o que se calcula não podem divergir (há um teste que compara
 * este cálculo com a matriz do Three.js).
 */

/** `ancoragem` prende (olhal, degrau, base), `passagem` guia a corda (polia, mosquetão), `carga` é onde a carga se pendura. */
export type UsoPontoAncoragem = 'ancoragem' | 'passagem' | 'carga'

export interface PontoAncoragem {
  readonly id: string
  readonly nome: string
  readonly uso: UsoPontoAncoragem
  /** Posição no referencial do objeto, antes da posição, rotação e escala dele na cena. */
  readonly local: VetorTabletop
  /** Pontos em série (degraus) aparecem menores, para não poluir a cena. */
  readonly secundario?: boolean
}

export const ROTULOS_USO_PONTO: Readonly<Record<UsoPontoAncoragem, string>> = {
  ancoragem: 'Ancoragem',
  passagem: 'Passagem',
  carga: 'Carga',
}

/** Cor de cada uso, na cena e no painel. Nunca só a cor: o painel sempre escreve o rótulo. */
export const CORES_USO_PONTO: Readonly<Record<UsoPontoAncoragem, string>> = {
  ancoragem: '#f97316',
  passagem: '#38bdf8',
  carga: '#22c55e',
}

/** Espaço entre os degraus da escada, em metros. */
export const ESPACO_DEGRAUS_ESCADA_M = 0.3

export type { DefinicaoParametro } from './parametros'
export { clamparParametro, definicoesDeParametros, parametrosDoObjeto } from './parametros'

const ponto = (
  id: string,
  nome: string,
  uso: UsoPontoAncoragem,
  local: VetorTabletop,
  secundario = false,
): PontoAncoragem => ({ id, nome, uso, local, ...(secundario ? { secundario } : {}) })

const rad = (graus: number) => (graus * Math.PI) / 180

/** Metade da largura da escada: os banzos (as laterais) ficam em x = ±0,25 m. */
export const MEIA_LARGURA_ESCADA_M = 0.25

/**
 * Os banzos de um ponto da escada (degrau, topo ou pé), no referencial dela:
 * é onde se passa um anel de fita de cada lado para ancorar na escada.
 */
export function banzosDoPontoDaEscada(local: VetorTabletop): readonly [VetorTabletop, VetorTabletop] {
  return [
    [-MEIA_LARGURA_ESCADA_M, local[1], local[2]],
    [MEIA_LARGURA_ESCADA_M, local[1], local[2]],
  ]
}

function pontosDaEscada(p: Record<string, number>, flexaoM = 0): readonly PontoAncoragem[] {
  const angulo = rad(p.anguloGraus!)
  const comprimento = p.comprimentoM!
  // a escada sobe pelo eixo que parte do pé e inclina para +Z: 90° é vertical, 0° é deitada
  const ao = (distancia: number): VetorTabletop => [0, distancia * Math.sin(angulo), distancia * Math.cos(angulo)]
  const pontos: PontoAncoragem[] = [
    ponto('pe', 'Pé da escada', 'ancoragem', ao(0)),
    ponto('topo', 'Topo da escada', 'ancoragem', ao(comprimento)),
  ]
  const degraus = Math.floor(comprimento / ESPACO_DEGRAUS_ESCADA_M + 1e-9)
  for (let i = 1; i <= degraus; i += 1) {
    pontos.push(ponto(`degrau-${i}`, `Degrau ${i}`, 'ancoragem', ao(i * ESPACO_DEGRAUS_ESCADA_M), true))
  }
  // um anel de fita em cada banzo, na altura de cada degrau: as pernas de um V na escada
  for (let i = 1; i <= degraus; i += 1) {
    const [esq, dir] = banzosDoPontoDaEscada(ao(i * ESPACO_DEGRAUS_ESCADA_M))
    pontos.push(ponto(`banzo-esq-${i}`, `Banzo esquerdo no degrau ${i}`, 'ancoragem', esq, true))
    pontos.push(ponto(`banzo-dir-${i}`, `Banzo direito no degrau ${i}`, 'ancoragem', dir, true))
  }
  // a escada flexiona para o lado do peso: os pontos acompanham a curva
  return flexaoM !== 0
    ? pontos.map((pt) => ({ ...pt, local: pontoFlexionado(pt.local, p.anguloGraus!, comprimento, flexaoM) }))
    : pontos
}

function pontosDoToco(p: Record<string, number>): readonly PontoAncoragem[] {
  // a fita dá a volta no tronco perto do chão; num toco baixo, no meio dele
  return [ponto('base', 'Base do toco', 'ancoragem', [0, Math.min(0.25, p.alturaM! / 2), 0])]
}

const PONTOS_MACA: readonly PontoAncoragem[] = [
  ponto('olhal', 'Olhal de içamento', 'carga', [0, 0.75, 0]),
  ponto('alca-cabeca', 'Alça da cabeça', 'carga', [0, 0.2, -0.95]),
  ponto('alca-pes', 'Alça dos pés', 'carga', [0, 0.2, 0.95]),
  ponto('alca-esq', 'Alça esquerda', 'carga', [-0.3, 0.2, 0]),
  ponto('alca-dir', 'Alça direita', 'carga', [0.3, 0.2, 0]),
]

const PONTOS_POLIA: readonly PontoAncoragem[] = [
  ponto('olhal', 'Olhal da polia', 'ancoragem', [0, 0.075, 0]),
  ponto('entrada', 'Entrada da corda', 'passagem', [-0.035, -0.02, 0]),
  ponto('saida', 'Saída da corda', 'passagem', [0.035, -0.02, 0]),
]

const PONTOS_MOSQUETAO: readonly PontoAncoragem[] = [ponto('passagem', 'Passagem do mosquetão', 'passagem', [0, 0, 0])]

/**
 * O ponto de ancoragem fixo (chapeleta com argola): a placa fica rente à
 * superfície em que está chumbado, com o +Y local saindo dela, e a argola em pé
 * sobre a placa. No chão ou numa laje a argola aponta para cima; numa parede,
 * gira-se a peça para o +Y sair da parede.
 */
export const GEOMETRIA_PONTO_ANCORAGEM = {
  placa: [0.1, 0.006, 0.05] as const,
  raioArgolaM: 0.035,
  tuboArgolaM: 0.007,
  centroArgolaY: 0.048,
} as const

/**
 * Placa de ancoragem (multiplicadora): pendura pelo furo de cima e oferece
 * vários furos embaixo, em arco, para clipar sistemas diferentes no mesmo
 * ponto de ancoragem sem amontoar mosquetões. A placa fica em pé no plano XY,
 * com a borda de baixo na origem (assenta no chão quando posta na mesa).
 */
export const GEOMETRIA_PLACA_ANCORAGEM = {
  larguraM: 0.2,
  alturaM: 0.16,
  espessuraM: 0.008,
  raioFuroM: 0.012,
  furoDeCima: [0, 0.115] as const,
  /** Furos de baixo, da esquerda para a direita (x, y), em arco. */
  furosDeBaixo: [
    [-0.066, 0.04],
    [-0.033, 0.024],
    [0, 0.018],
    [0.033, 0.024],
    [0.066, 0.04],
  ] as ReadonlyArray<readonly [number, number]>,
} as const

const PONTOS_PLACA_ANCORAGEM: readonly PontoAncoragem[] = [
  ponto('topo', 'Furo de cima (pendura a placa)', 'ancoragem', [GEOMETRIA_PLACA_ANCORAGEM.furoDeCima[0], GEOMETRIA_PLACA_ANCORAGEM.furoDeCima[1], 0]),
  ...GEOMETRIA_PLACA_ANCORAGEM.furosDeBaixo.map(([x, y], i) => ponto(`furo-${i + 1}`, `Furo ${i + 1} da placa`, 'ancoragem', [x, y, 0])),
]

/**
 * Descensor, em pé no plano XY, com o olhal embaixo: no oito, a corda entra
 * pelo anel grande (em cima) e sai pelo lado do pescoço; no autoblocante (tipo
 * ID), entra em cima e sai embaixo, do lado do freio. As medidas são em
 * volta do centro da peça; `base` sobe tudo para a borda de baixo assentar na
 * origem (posto no chão, ele não afunda).
 */
export const GEOMETRIA_DESCENSOR = {
  base: { oito: 0.085, autoblocante: 0.082 },
  oito: { olhal: [0, -0.06, 0], entrada: [0, 0.085, 0], saida: [0.034, 0.012, 0] },
  autoblocante: { olhal: [-0.018, -0.06, 0], entrada: [0.012, 0.075, 0], saida: [0.03, -0.07, 0] },
} as const

const pontosDoDescensor = (p: Record<string, number>): readonly PontoAncoragem[] => {
  const auto = p.tipoDescensor === 1
  const g = auto ? GEOMETRIA_DESCENSOR.autoblocante : GEOMETRIA_DESCENSOR.oito
  const b = auto ? GEOMETRIA_DESCENSOR.base.autoblocante : GEOMETRIA_DESCENSOR.base.oito
  const subir = (v: readonly [number, number, number]): VetorTabletop => [v[0], v[1] + b, v[2]]
  return [
    ponto('olhal', 'Olhal do descensor', 'ancoragem', subir(g.olhal)),
    ponto('entrada', 'Entrada da corda', 'passagem', subir(g.entrada)),
    ponto('saida', 'Saída da corda (freio)', 'passagem', subir(g.saida)),
  ]
}

/** Protetor de corda: a manga que fica na borda, com a corda passando por dentro, na origem (a quina). */
const PONTOS_PROTETOR: readonly PontoAncoragem[] = [ponto('passagem', 'Passagem pelo protetor', 'passagem', [0, 0, 0])]

/** A ancoragem por envolvimento oferece um ponto: o mosquetão onde as pernas da fita se juntam. */
const pontosDoEnvolvimento = (p: Record<string, number>): readonly PontoAncoragem[] => [
  ponto('mestre', 'Mosquetão da fita', 'ancoragem', geometriaDoEnvolvimento({ tipo: 'envolvimento', parametros: p }).mestre),
]

const PONTOS_PONTO_ANCORAGEM: readonly PontoAncoragem[] = [
  ponto('olhal', 'Argola do ponto de ancoragem', 'ancoragem', [0, GEOMETRIA_PONTO_ANCORAGEM.centroArgolaY, 0]),
]

const PONTOS_ALCA_FITA: readonly PontoAncoragem[] = [
  ponto('olhal', 'Alça de fita', 'ancoragem', [0, 0, 0]),
]

const PONTOS_BLOQUEADOR: readonly PontoAncoragem[] = [
  ponto('entrada', 'Entrada da corda', 'passagem', [0, 0.07, 0]),
  ponto('saida', 'Saída da corda', 'passagem', [0, -0.05, 0]),
  ponto('olhal', 'Olhal do bloqueador', 'ancoragem', [0, -0.075, 0]),
]

/**
 * Viatura (ABT). Coordenadas do modelo desenhado em `CaminhaoBombeiros3D`:
 * a frente aponta para -X, a traseira para +X, e os lados esquerdo e direito
 * (olhando para a frente) ficam em +Z e -Z.
 */
const PONTOS_VIATURA: readonly PontoAncoragem[] = [
  ponto('para-choque-esq', 'Para-choque dianteiro esquerdo', 'ancoragem', [-3.1, 0.82, 0.9]),
  ponto('para-choque-dir', 'Para-choque dianteiro direito', 'ancoragem', [-3.1, 0.82, -0.9]),
  ponto('degrau-traseiro', 'Degrau traseiro', 'ancoragem', [3.3, 0.75, 0]),
  ponto('topo-traseiro-esq', 'Topo traseiro esquerdo', 'ancoragem', [3.1, 2.78, 0.95]),
  ponto('topo-traseiro-dir', 'Topo traseiro direito', 'ancoragem', [3.1, 2.78, -0.95]),
  ponto('escada-teto', 'Escada do teto', 'ancoragem', [1.25, 2.84, -0.55]),
  // cubo das rodas (eixo): ponto fixo robusto para ancorar o sistema de redução quando não há toco nem árvore
  ponto('roda-traseira-esq', 'Roda traseira esquerda (eixo)', 'ancoragem', [2.1, 0.55, 1.25]),
  ponto('roda-traseira-dir', 'Roda traseira direita (eixo)', 'ancoragem', [2.1, 0.55, -1.25]),
  ponto('roda-dianteira-esq', 'Roda dianteira esquerda (eixo)', 'ancoragem', [-1.85, 0.55, 1.2]),
  ponto('roda-dianteira-dir', 'Roda dianteira direita (eixo)', 'ancoragem', [-1.85, 0.55, -1.2]),
]

const PONTOS_ARVORE: readonly PontoAncoragem[] = [
  ponto('base', 'Base da árvore', 'ancoragem', [0, 0.4, 0]),
  ponto('meio', 'Meio do tronco', 'ancoragem', [0, 1.55, 0]),
]

const PONTOS_POSTE: readonly PontoAncoragem[] = [
  ponto('base', 'Base do poste', 'ancoragem', [0, 0.4, 0]),
  ponto('meio', 'Meio do poste', 'ancoragem', [0, 2.25, 0]),
]

/**
 * Terreno: a corda não prende no chão, ela passa por ele. Por isso os pontos
 * são de passagem: o pé da rampa e a borda onde a corda dobra.
 */
function pontosDoTerreno(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const perfil = perfilDoTerreno(objeto)
  if (!perfil) return SEM_PONTOS
  const { pontos } = perfil
  if (objeto.tipo === 'vala') {
    // margens: o canto de cima da parede de cada lado
    const [, , esquerda, , , direita] = pontos
    return [
      ponto('margem-a', 'Margem A da vala', 'passagem', [0, esquerda![1], esquerda![0]]),
      ponto('margem-b', 'Margem B da vala', 'passagem', [0, direita![1], direita![0]]),
    ]
  }
  const [pe, borda] = pontos
  const nome = objeto.tipo === 'barranco' ? 'barranco' : 'talude'
  return [
    ponto('pe', `Pé do ${nome}`, 'passagem', [0, pe![1], pe![0]]),
    ponto('borda-superior', `Borda superior do ${nome}`, 'passagem', [0, borda![1], borda![0]]),
  ]
}

/**
 * Nó fiel (volta do fiel, "clove hitch"): amarra a ponta da corda direto numa
 * barra, como o degrau ou o banzo da escada. A corda sai do nó pelo ponto
 * `olhal`, no centro dele; a peça fica sobre o ponto da estrutura em que foi amarrada.
 */
const PONTOS_NO_FIEL: readonly PontoAncoragem[] = [ponto('olhal', 'Ponta amarrada pelo fiel', 'ancoragem', [0, 0, 0])]

const PONTOS_NO_OITO: readonly PontoAncoragem[] = [ponto('passagem', 'Passagem do nó', 'passagem', [0, 0, 0])]

/**
 * Canal: o topo da mureta de cada lado, a banqueta, a parede e o leito. São
 * pontos de passagem: a corda desce por eles, não se prende neles.
 */
function pontosDoCanal(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const m = medidasDoCanal(parametrosDoObjeto(objeto))
  const pontos: PontoAncoragem[] = [
    ponto('borda-a', 'Topo da mureta (lado da banqueta)', 'passagem', [0, m.muretaM, m.paredeZ - ESPESSURA_MURETA_M / 2]),
  ]
  if (m.banquetaM > 0) {
    pontos.push(ponto('banqueta', 'Banqueta', 'passagem', [0, m.yBanquetaM, -(m.meioLeitoM + m.banquetaM / 2)]))
  }
  pontos.push(
    ponto('parede', 'Parede do lado da banqueta', 'passagem', [0, (m.banquetaM > 0 ? m.yBanquetaM : m.yLeitoM) / 2, m.paredeZ]),
    ponto('leito', 'Leito', 'passagem', [0, m.yLeitoM, 0]),
    ponto('borda-b', 'Topo da mureta (margem oposta)', 'passagem', [0, m.muretaM, m.topoDireitoZ + ESPESSURA_MURETA_M / 2]),
  )
  return pontos
}

function pontosDaCisterna(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const f = formaDaCisterna(objeto)!
  return [
    ponto('boca', 'Boca da cisterna', 'passagem', [0, 0, 0]),
    ponto('borda', 'Borda da boca', 'passagem', [f.raioFuroM, 0, 0]),
    ponto('fundo', 'Fundo', 'passagem', [0, -f.profundidadeM, 0]),
  ]
}

/**
 * Ponte: a origem é o centro do tabuleiro, no nível do topo; o vão corre pelo
 * eixo Z e a largura pelo X. Guarda-corpo, viga e pilar servem de ancoragem; a
 * cabeceira (a ponta do tabuleiro, sobre a parede) é de passagem.
 */
function pontosDaPonte(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const p = parametrosDoObjeto(objeto)
  const meioVao = p.vaoM! / 2
  const meiaLargura = p.larguraM! / 2
  const pontos: PontoAncoragem[] = [
    ponto('cabeceira-a', 'Cabeceira A', 'passagem', [0, 0, -meioVao]),
    ponto('cabeceira-b', 'Cabeceira B', 'passagem', [0, 0, meioVao]),
  ]
  if (p.guardaCorpoM! > 0) {
    pontos.push(
      ponto('guarda-corpo-a', 'Guarda-corpo esquerdo', 'ancoragem', [-meiaLargura, p.guardaCorpoM!, 0]),
      ponto('guarda-corpo-b', 'Guarda-corpo direito', 'ancoragem', [meiaLargura, p.guardaCorpoM!, 0]),
    )
  }
  const xViga = Math.max(0, meiaLargura - 0.6)
  pontos.push(
    ponto('viga-a', 'Viga esquerda', 'ancoragem', [-xViga, -p.espessuraM!, 0]),
    ponto('viga-b', 'Viga direita', 'ancoragem', [xViga, -p.espessuraM!, 0]),
  )
  if (p.pilarAlturaM! > 0) pontos.push(ponto('pilar', 'Pilar central', 'ancoragem', [0, -p.espessuraM!, 0]))
  return pontos
}

const PONTOS_POLIA_DUPLA: readonly PontoAncoragem[] = [
  ponto('olhal', 'Olhal da polia dupla', 'ancoragem', [0, 0.075, 0]),
  ponto('entrada-1', 'Entrada da roldana 1', 'passagem', [-0.035, -0.02, -0.022]),
  ponto('saida-1', 'Saída da roldana 1', 'passagem', [0.035, -0.02, -0.022]),
  ponto('entrada-2', 'Entrada da roldana 2', 'passagem', [-0.035, -0.02, 0.022]),
  ponto('saida-2', 'Saída da roldana 2', 'passagem', [0.035, -0.02, 0.022]),
]

/**
 * Trabalhador em altura: o engate dorsal do cinturão, nas costas, a `engateAosPesM`
 * acima dos pés (a origem do objeto), e os pés. O talabarte sai do engate. A
 * frente do trabalhador é +Z; o engate fica 12 cm atrás do eixo.
 */
/**
 * O trabalhador em pé tem a pelve a 0,92 m dos pés. Sentado na cadeirinha
 * (acesso por corda), a pelve desce para 0,55 m e o tronco desce junto: o
 * engate dorsal e o ventral acompanham.
 */
export const PELVE_EM_PE_M = 0.92
export const PELVE_SENTADO_M = 0.55

export const descidaDoTronco = (p: Record<string, number>) => (p.sentadoNaCadeirinha === 1 ? PELVE_SENTADO_M - PELVE_EM_PE_M : 0)

function pontosDoTrabalhador(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const p = parametrosDoObjeto(objeto)
  const d = descidaDoTronco(p)
  return [
    ponto('engate-dorsal', 'Engate dorsal do cinturão', 'carga', [0, p.engateAosPesM! + d, -0.12]),
    ponto('engate-ventral', 'Engate ventral (na frente da cintura)', 'carga', [0, 1.0 + d, 0.14]),
    ponto('pes', 'Pés', 'carga', [0, 0, 0]),
  ]
}

/**
 * Torre: o anel no centro do topo, o meio e a base no montante de um canto, e
 * duas ancoragens por patamar, a 2 m do piso (acima do engate de quem está ali):
 * no montante do canto oposto ao alçapão e no montante do canto do alçapão.
 */
function pontosDaTorre(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const { alturaM, larguraM, espacoPatamarM } = parametrosDoObjeto(objeto)
  const canto = larguraM! / 2
  const patamares = alturasDosPatamares(alturaM!, espacoPatamarM!)
  return [
    ponto('topo', 'Topo da torre', 'ancoragem', [0, alturaM!, 0]),
    ponto('meio', 'Meio da torre', 'ancoragem', [canto, alturaM! / 2, canto]),
    ponto('base', 'Base da torre', 'ancoragem', [0, 0.1, 0]),
    ...patamares.flatMap((h, i) => {
      const y = Math.min(alturaM!, h + ANCORAGEM_ACIMA_DO_PATAMAR_M)
      const nivel = String(h).replace('.', ',')
      const [sx, sz] = cantoDoAlcapao(i)
      return [
        ponto(`patamar-${i + 1}`, `Ancoragem do patamar ${i + 1} (${nivel} m)`, 'ancoragem', [-sx * canto, y, -sz * canto]),
        // o montante do canto do alçapão: quem se prende ali pode cair pelo alçapão no patamar de baixo
        ponto(`patamar-${i + 1}-alcapao`, `Ancoragem do patamar ${i + 1} junto do alçapão (${nivel} m)`, 'ancoragem', [sx * canto, y, sz * canto]),
      ]
    }),
  ]
}

/**
 * Sistema de vantagem mecânica: o bloco fixo (a ancoragem) em x = 0, o bloco
 * móvel (a carga) em x = curso e a ponta livre, por onde se puxa, para o lado
 * negativo de X.
 */
function pontosDoSistemaVM(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const p = parametrosDoObjeto(objeto)
  const d = disposicaoDoSistema(p.razao!, p.cursoM!)
  return [
    ponto('ancoragem', 'Olhal do bloco fixo', 'ancoragem', [0, 0, 0]),
    ponto('carga', 'Olhal do bloco móvel', 'carga', [p.cursoM!, 0, 0]),
    ponto('ponta-livre', 'Ponta livre (onde se puxa)', 'passagem', [d.pontaLivre.ateX, 0, d.pontaLivre.z]),
  ]
}

function pontosDoAnimal(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const p = parametrosDoObjeto(objeto)
  const deitado = p.postura === 1
  const nomeTipo = objeto.tipo === 'cavalo' ? 'do cavalo' : 'da vaca'
  if (deitado) {
    return [
      ponto('dorso', `Olhal dorsal de içamento ${nomeTipo}`, 'carga', [0, 0.55, 0]),
      ponto('peitoral', `Peitoral ${nomeTipo}`, 'ancoragem', [0, 0.35, 0.7]),
      ponto('garupa', `Garupa ${nomeTipo}`, 'ancoragem', [0, 0.4, -0.7]),
      ponto('cabeca', `Cabeça / Cabresto ${nomeTipo}`, 'ancoragem', [0, 0.3, 1.1]),
    ]
  }
  return [
    ponto('dorso', `Olhal dorsal de içamento ${nomeTipo}`, 'carga', [0, 1.55, 0]),
    ponto('peitoral', `Peitoral ${nomeTipo}`, 'ancoragem', [0, 1.15, 0.7]),
    ponto('garupa', `Garupa ${nomeTipo}`, 'ancoragem', [0, 1.4, -0.7]),
    ponto('cabeca', `Cabeça / Cabresto ${nomeTipo}`, 'ancoragem', [0, 1.75, 1.0]),
  ]
}

function pontosDaEstaca(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  const p = parametrosDoObjeto(objeto)
  const cfg = p.configuracao ?? 0
  const pontos: PontoAncoragem[] = [
    ponto('base', 'Base da estaca (rente ao solo)', 'ancoragem', [0, 0.05, 0]),
    ponto('topo', 'Topo da estaca', 'ancoragem', [0, 0.5, -0.2]),
  ]
  if (cfg >= 1) {
    pontos.push(
      ponto('base-2', 'Base da estaca 2', 'ancoragem', [0, 0.05, -1.0]),
      ponto('topo-2', 'Topo da estaca 2', 'ancoragem', [0, 0.5, -1.2]),
    )
  }
  if (cfg >= 2) {
    pontos.push(
      ponto('base-3', 'Base da estaca 3', 'ancoragem', [0, 0.05, -2.0]),
      ponto('topo-3', 'Topo da estaca 3', 'ancoragem', [0, 0.5, -2.2]),
    )
  }
  return pontos
}

const SEM_PONTOS: readonly PontoAncoragem[] = []

/** Os pontos de ancoragem do objeto, no referencial dele. Objetos sem ancoragem devolvem lista vazia. */
export function pontosDeAncoragem(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): readonly PontoAncoragem[] {
  switch (objeto.tipo) {
    case 'escada':
      return pontosDaEscada(parametrosDoObjeto(objeto), objeto.parametros?.flexaoM ?? 0)
    case 'toco':
      return pontosDoToco(parametrosDoObjeto(objeto))
    case 'maca':
      return PONTOS_MACA
    case 'polia':
      return PONTOS_POLIA
    case 'mosquetao':
      return PONTOS_MOSQUETAO
    case 'noFiel':
      return PONTOS_NO_FIEL
    case 'noOito':
      return PONTOS_NO_OITO
    case 'bloqueador':
      return PONTOS_BLOQUEADOR
    case 'caminhaoBombeiros':
      return PONTOS_VIATURA
    case 'arvore':
      return PONTOS_ARVORE
    case 'poste':
      return PONTOS_POSTE
    case 'talude':
    case 'barranco':
    case 'vala':
      return pontosDoTerreno(objeto)
    case 'canal':
      return pontosDoCanal(objeto)
    case 'cisterna':
      return pontosDaCisterna(objeto)
    case 'ponte':
      return pontosDaPonte(objeto)
    case 'poliaDupla':
      return PONTOS_POLIA_DUPLA
    case 'sistemaVM':
      return pontosDoSistemaVM(objeto)
    case 'trabalhador':
      return pontosDoTrabalhador(objeto)
    case 'torre':
      return pontosDaTorre(objeto)
    case 'pontoAncoragem':
      return parametrosDoObjeto(objeto).alcaFita === 1 ? PONTOS_ALCA_FITA : PONTOS_PONTO_ANCORAGEM
    case 'placaAncoragem':
      return PONTOS_PLACA_ANCORAGEM
    case 'envolvimento':
      return pontosDoEnvolvimento(parametrosDoObjeto(objeto))
    case 'descensor':
      return pontosDoDescensor(parametrosDoObjeto(objeto))
    case 'protetorCorda':
      return PONTOS_PROTETOR
    case 'cavalo':
    case 'vaca':
      return pontosDoAnimal(objeto)
    case 'estaca':
      return pontosDaEstaca(objeto)
    default:
      return SEM_PONTOS
  }
}

/**
 * Leva um ponto local para o mundo, na mesma ordem em que a cena aplica a
 * transformação do objeto: escala, depois rotação em ordem YXZ (a mesma do
 * `<group rotation={[x, y, z, 'YXZ']}>` da cena), depois posição.
 */
function localParaMundo(
  local: VetorTabletop,
  transformacao: ObjetoTabletop['transformacao'],
): VetorTabletop {
  const [ex, ey, ez] = transformacao.escala
  let x = local[0] * ex
  let y = local[1] * ey
  let z = local[2] * ez

  const [rx, ry, rz] = transformacao.rotacao
  // Z, depois X, depois Y: é a matriz Ry · Rx · Rz aplicada ao vetor
  const cz = Math.cos(rz)
  const sz = Math.sin(rz)
  ;[x, y] = [x * cz - y * sz, x * sz + y * cz]
  const cx = Math.cos(rx)
  const sx = Math.sin(rx)
  ;[y, z] = [y * cx - z * sx, y * sx + z * cx]
  const cy = Math.cos(ry)
  const sy = Math.sin(ry)
  ;[x, z] = [x * cy + z * sy, -x * sy + z * cy]

  const [px, py, pz] = transformacao.posicao
  return [x + px, y + py, z + pz]
}

export interface PontoNoMundo extends PontoAncoragem {
  readonly mundo: VetorTabletop
}

/** Todos os pontos do objeto, já na posição em que estão na cena. */
export function pontosNoMundo(objeto: ObjetoTabletop): readonly PontoNoMundo[] {
  return pontosDeAncoragem(objeto).map((p) => ({ ...p, mundo: localParaMundo(p.local, objeto.transformacao) }))
}

/** A posição, no mundo, de um ponto do objeto. Erro claro se o ponto não existe. */
export function pontoNoMundo(objeto: ObjetoTabletop, idPonto: string): VetorTabletop {
  const achado = pontosDeAncoragem(objeto).find((p) => p.id === idPonto)
  if (!achado) throw new Error(`O objeto "${objeto.nome}" não tem o ponto de ancoragem "${idPonto}".`)
  return localParaMundo(achado.local, objeto.transformacao)
}

/**
 * O inverso de `localParaMundo`: leva um ponto do mundo para o referencial do
 * objeto (tira a posição, desfaz a rotação YXZ e divide pela escala).
 */
export function mundoParaLocalDoObjeto(mundo: VetorTabletop, transformacao: ObjetoTabletop['transformacao']): VetorTabletop {
  const [px, py, pz] = transformacao.posicao
  let x = mundo[0] - px
  let y = mundo[1] - py
  let z = mundo[2] - pz
  const [rx, ry, rz] = transformacao.rotacao
  // a ordem inversa: desfaz Y, depois X, depois Z
  const cy = Math.cos(-ry)
  const sy = Math.sin(-ry)
  ;[x, z] = [x * cy + z * sy, -x * sy + z * cy]
  const cx = Math.cos(-rx)
  const sx = Math.sin(-rx)
  ;[y, z] = [y * cx - z * sx, y * sx + z * cx]
  const cz = Math.cos(-rz)
  const sz = Math.sin(-rz)
  ;[x, y] = [x * cz - y * sz, x * sz + y * cz]
  const [ex, ey, ez] = transformacao.escala
  return [x / ex, y / ey, z / ez]
}

export { localParaMundo }

/** A posição, no mundo, do ponto referenciado; `null` se o objeto ou o ponto não existem mais. */
export function localizarPontoNoMundo(cena: CenaTabletop, referencia: ReferenciaPonto): VetorTabletop | null {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  if (!objeto) return null
  const achado = pontosDeAncoragem(objeto).find((p) => p.id === referencia.idPonto)
  if (!achado) return null
  return localParaMundo(achado.local, objeto.transformacao)
}
